import { addDays, addMinutes } from "date-fns";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import type { Appointment, BlockedDate, Schedule, Service } from "@prisma/client";
import { getSchemaDayOfWeek, zonedWallToUtc } from "./timezone.js";
import { appointmentBlocksSlot } from "./appointment-availability.js";

export type CourtSlotContext = {
  courtDurationMinutes: number;
  schedules: Schedule[];
  blockedDates: BlockedDate[];
  appointments: (Pick<Appointment, "startTime" | "endTime" | "status"> & {
    depositStatus?: string | null;
    depositHoldExpiresAt?: Date | null;
  })[];
  timeZone: string;
  dateStr: string;
  now?: Date;
};

/** Genera inicios posibles (UTC) para una cancha en un día dado. */
export function buildCourtAvailableSlots(ctx: CourtSlotContext): Date[] {
  const { courtDurationMinutes, schedules, blockedDates, appointments, timeZone, dateStr } = ctx;
  const now = ctx.now ?? new Date();
  const dow = getSchemaDayOfWeek(dateStr, timeZone);

  if (isBlockedDay(dateStr, timeZone, blockedDates)) return [];

  const daySchedules = schedules.filter((s) => s.dayOfWeek === dow);
  const slots: Date[] = [];

  for (const sch of daySchedules) {
    const startParts = parseHHmm(sch.startTime);
    const endParts = parseHHmm(sch.endTime);

    let cursor = zonedWallToUtc(
      dateStr,
      `${String(startParts.h).padStart(2, "0")}:${String(startParts.m).padStart(2, "0")}`,
      timeZone,
    );
    const endBoundary = zonedWallToUtc(
      dateStr,
      `${String(endParts.h).padStart(2, "0")}:${String(endParts.m).padStart(2, "0")}`,
      timeZone,
    );

    while (cursor < endBoundary) {
      const slotEnd = addMinutes(cursor, courtDurationMinutes);
      if (slotEnd > endBoundary) break;
      if (!overlaps(cursor, slotEnd, appointments, now)) {
        slots.push(cursor);
      }
      cursor = addMinutes(cursor, courtDurationMinutes);
    }
  }

  return slots;
}

type SlotContext = {
  schedules: Schedule[];
  blockedDates: BlockedDate[];
  appointments: (Pick<Appointment, "startTime" | "endTime" | "status"> & {
    depositStatus?: string | null;
    depositHoldExpiresAt?: Date | null;
  })[];
  timeZone: string;
  dateStr: string;
  service: Pick<Service, "duration">;
  /** Cuando el servicio ofrece múltiples duraciones, el cliente eligió esta. */
  durationOverride?: number;
  /** Referencia para liberar holds de seña vencidos (default: ahora). */
  now?: Date;
};

/** Convierte el campo JSON `durationOptions` del modelo Service en un arreglo de enteros válidos. */
export function parseDurationOptions(raw: unknown): number[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is number => typeof v === "number" && Number.isInteger(v) && v >= 5 && v <= 24 * 60);
}

function parseHHmm(s: string): { h: number; m: number } {
  const [h, m] = s.split(":").map(Number);
  return { h, m };
}

function isBlockedDay(dateStr: string, timeZone: string, blocked: BlockedDate[]): boolean {
  return blocked.some((b) => {
    const startKey = formatInTimeZone(b.date, timeZone, "yyyy-MM-dd");
    const end = b.endDate ?? b.date;
    const endKey = formatInTimeZone(end, timeZone, "yyyy-MM-dd");
    return dateStr >= startKey && dateStr <= endKey;
  });
}

function overlaps(
  start: Date,
  end: Date,
  existing: SlotContext["appointments"],
  now: Date,
): boolean {
  return existing.some((a) => {
    if (
      !appointmentBlocksSlot(
        {
          status: a.status,
          depositStatus: a.depositStatus ?? null,
          depositHoldExpiresAt: a.depositHoldExpiresAt ?? null,
        },
        now,
      )
    ) {
      return false;
    }
    return a.startTime < end && a.endTime > start;
  });
}

/** Genera inicios posibles (UTC) para un día y servicio. */
export function buildAvailableSlots(ctx: SlotContext): Date[] {
  const { schedules, blockedDates, appointments, timeZone, dateStr, service } = ctx;
  const now = ctx.now ?? new Date();
  const dow = getSchemaDayOfWeek(dateStr, timeZone);
  const effectiveDuration = ctx.durationOverride ?? service.duration;

  if (isBlockedDay(dateStr, timeZone, blockedDates)) return [];

  const daySchedules = schedules.filter((s) => s.dayOfWeek === dow);
  const slots: Date[] = [];

  for (const sch of daySchedules) {
    const startParts = parseHHmm(sch.startTime);
    const endParts = parseHHmm(sch.endTime);

    let cursor = zonedWallToUtc(
      dateStr,
      `${String(startParts.h).padStart(2, "0")}:${String(startParts.m).padStart(2, "0")}`,
      timeZone,
    );
    const endBoundary = zonedWallToUtc(
      dateStr,
      `${String(endParts.h).padStart(2, "0")}:${String(endParts.m).padStart(2, "0")}`,
      timeZone,
    );

    const gap = sch.interval ?? 0;
    const step = sch.slotDuration + gap;
    while (cursor < endBoundary) {
      const slotEnd = addMinutes(cursor, effectiveDuration);
      if (slotEnd > endBoundary) break;
      if (!overlaps(cursor, slotEnd, appointments, now)) {
        slots.push(cursor);
      }
      cursor = addMinutes(cursor, step);
    }
  }

  return slots;
}

/** Días del mes con al menos un turno libre para una cancha (misma lógica que GET /api/public/availability para courts). */
export function listDatesWithCourtSlotsInMonth(opts: {
  monthYyyyMm: string;
  timeZone: string;
  schedules: Schedule[];
  blockedDates: BlockedDate[];
  appointments: CourtSlotContext["appointments"];
  courtDurationMinutes: number;
  /** yyyy-MM-dd en timeZone; fechas anteriores se ignoran. */
  minDateStr: string;
  now?: Date;
}): string[] {
  const { monthYyyyMm, timeZone, schedules, blockedDates, appointments: allAppts, courtDurationMinutes, minDateStr, now } = opts;
  const dates = enumerateDatesInMonth(monthYyyyMm, timeZone);
  const available: string[] = [];
  const t = now ?? new Date();

  for (const dateStr of dates) {
    if (dateStr < minDateStr) continue;
    const dayStart = fromZonedTime(`${dateStr}T00:00:00`, timeZone);
    const dayEnd = fromZonedTime(`${dateStr}T23:59:59.999`, timeZone);
    const dayAppts = allAppts.filter((a) => a.startTime < dayEnd && a.endTime > dayStart);
    const slots = buildCourtAvailableSlots({
      courtDurationMinutes,
      schedules,
      blockedDates,
      appointments: dayAppts,
      timeZone,
      dateStr,
      now: t,
    });
    if (slots.length > 0) available.push(dateStr);
  }

  return available;
}

/** Días calendario (yyyy-MM-dd) de un mes en la zona del profesional. */
export function enumerateDatesInMonth(monthYyyyMm: string, timeZone: string): string[] {
  const firstWall = `${monthYyyyMm}-01`;
  let cur = fromZonedTime(`${firstWall}T12:00:00`, timeZone);
  const out: string[] = [];
  for (let i = 0; i < 35; i++) {
    const inM = formatInTimeZone(cur, timeZone, "yyyy-MM");
    if (inM !== monthYyyyMm) break;
    out.push(formatInTimeZone(cur, timeZone, "yyyy-MM-dd"));
    cur = addDays(cur, 1);
  }
  return out;
}

/**
 * Genera inicios posibles para un día y servicio, considerando que la agenda puede tener más de
 * un atendente (`AgendaStaff`). Un horario está disponible si AL MENOS UNO de los atendentes está
 * libre en ese momento — no basta con que la agenda "como bloque" esté ocupada, ya que dos
 * atendentes distintos pueden atender en simultáneo sin conflicto (mismo criterio de
 * scope courtId → staffMemberId → professionalId que el resto del sistema).
 *
 * Los turnos sin atendente asignado (`staffMemberId` null) se consideran conservadoramente como
 * bloqueantes para TODOS los atendentes, ya que no se sabe a cuál corresponden.
 */
export function buildAvailableSlotsAcrossStaff(ctx: {
  schedules: Schedule[];
  blockedDates: BlockedDate[];
  appointments: (Pick<Appointment, "startTime" | "endTime" | "status"> & {
    depositStatus?: string | null;
    depositHoldExpiresAt?: Date | null;
    staffMemberId?: string | null;
  })[];
  /** Ids de los atendentes activos de la agenda. Vacío = tratar la agenda como un solo recurso. */
  staffIds: string[];
  timeZone: string;
  dateStr: string;
  service: Pick<Service, "duration">;
  durationOverride?: number;
  now?: Date;
}): Date[] {
  const { appointments, staffIds, ...rest } = ctx;

  if (staffIds.length === 0) {
    return buildAvailableSlots({ ...rest, appointments });
  }

  const unassigned = appointments.filter((a) => !a.staffMemberId);
  const bySlot = new Map<number, Date>();
  for (const staffId of staffIds) {
    const forThisStaff = appointments.filter((a) => a.staffMemberId === staffId);
    const slots = buildAvailableSlots({ ...rest, appointments: [...unassigned, ...forThisStaff] });
    for (const s of slots) bySlot.set(s.getTime(), s);
  }
  return Array.from(bySlot.values()).sort((a, b) => a.getTime() - b.getTime());
}

/** Días del mes con al menos un turno libre (misma lógica que GET /api/public/availability). */
export function listDatesWithSlotsInMonth(opts: {
  monthYyyyMm: string;
  timeZone: string;
  schedules: Schedule[];
  blockedDates: BlockedDate[];
  appointments: SlotContext["appointments"];
  service: Pick<Service, "duration">;
  /** Duración override cuando el servicio ofrece múltiples opciones. */
  durationOverride?: number;
  /** yyyy-MM-dd en timeZone; fechas anteriores se ignoran. */
  minDateStr: string;
  now?: Date;
}): string[] {
  const { monthYyyyMm, timeZone, schedules, blockedDates, appointments: allAppts, service, durationOverride, minDateStr, now } = opts;
  const dates = enumerateDatesInMonth(monthYyyyMm, timeZone);
  const available: string[] = [];
  const t = now ?? new Date();

  for (const dateStr of dates) {
    if (dateStr < minDateStr) continue;
    const dayStart = fromZonedTime(`${dateStr}T00:00:00`, timeZone);
    const dayEnd = fromZonedTime(`${dateStr}T23:59:59.999`, timeZone);
    const dayAppts = allAppts.filter((a) => a.startTime < dayEnd && a.endTime > dayStart);
    const slots = buildAvailableSlots({
      schedules,
      blockedDates,
      appointments: dayAppts,
      timeZone,
      dateStr,
      service,
      durationOverride,
      now: t,
    });
    if (slots.length > 0) available.push(dateStr);
  }

  return available;
}

/** Variante de listDatesWithSlotsInMonth consciente de múltiples atendentes (ver buildAvailableSlotsAcrossStaff). */
export function listDatesWithSlotsInMonthAcrossStaff(opts: {
  monthYyyyMm: string;
  timeZone: string;
  schedules: Schedule[];
  blockedDates: BlockedDate[];
  appointments: (SlotContext["appointments"][number] & { staffMemberId?: string | null })[];
  staffIds: string[];
  service: Pick<Service, "duration">;
  durationOverride?: number;
  minDateStr: string;
  now?: Date;
}): string[] {
  const { monthYyyyMm, timeZone, schedules, blockedDates, appointments: allAppts, staffIds, service, durationOverride, minDateStr, now } = opts;
  const dates = enumerateDatesInMonth(monthYyyyMm, timeZone);
  const available: string[] = [];
  const t = now ?? new Date();

  for (const dateStr of dates) {
    if (dateStr < minDateStr) continue;
    const dayStart = fromZonedTime(`${dateStr}T00:00:00`, timeZone);
    const dayEnd = fromZonedTime(`${dateStr}T23:59:59.999`, timeZone);
    const dayAppts = allAppts.filter((a) => a.startTime < dayEnd && a.endTime > dayStart);
    const slots = buildAvailableSlotsAcrossStaff({
      schedules,
      blockedDates,
      appointments: dayAppts,
      staffIds,
      timeZone,
      dateStr,
      service,
      durationOverride,
      now: t,
    });
    if (slots.length > 0) available.push(dateStr);
  }

  return available;
}
